import { ChangeEvent, useState } from 'react';
import {
  classnames,
  isEmailValid,
  noEmptyFields,
  noErrors,
} from 'helpers/utils';
import { ChevronRightIcon } from 'assets/icons';
import { Button, ButtonSize, ButtonStyle, ButtonType } from 'common/button';
import { Input } from 'common/input';
import { captureEvent } from 'posthog';
import { trackCustomEvent } from 'helpers/analytics';
import { UserController } from 'networking/controllers/user-controller';
import { Notification } from 'common/notification';
import { Spinner } from 'common/spinner';
import styles from './reset-password-steps.module.scss';

const mandatoryFields: MandatoryFieldsType = {
  email: '',
};

type ResetPasswordStep1Props = {
  notifSuccess: () => void;
  email: string;
  setEmail: (email: string) => void;
  onClickBack: () => void;
};

const ResetPasswordStep1: React.FC<ResetPasswordStep1Props> = ({
  notifSuccess,
  email,
  setEmail,
  onClickBack,
}) => {
  const [errors, setErrors] = useState<MandatoryFieldsType>(mandatoryFields);
  const [showError, setShowError] = useState(false);
  const [fetching, setFetching] = useState(false);

  const backToLogin = () => (
    <div className={styles.containerBack}>
      <ChevronRightIcon className={styles.backIcon} />
      <Button
        buttonSize={ButtonSize.Small}
        buttonStyle={ButtonStyle.PrimaryGhost}
        onClick={onClickBack}
        className={styles.backPassword}
      >
        Back to Login
      </Button>
    </div>
  );

  const validateEmail = (value: string) => {
    let emailMessage = '';

    emailMessage = value.trim() === '' ? "email shouldn't be empty" : '';
    setErrors((prevState) => ({
      ...prevState,
      email: emailMessage.toUpperCase(),
    }));

    if (emailMessage) return;

    emailMessage = !isEmailValid(value) ? 'invalid email' : '';
    setErrors((prevState) => ({
      ...prevState,
      email: emailMessage.toUpperCase(),
    }));
  };

  const handleSubmit = async () => {
    validateEmail(email);

    if (!noErrors(errors)) {
      return;
    }

    setFetching(true);

    captureEvent('sendPasswordResetEmail');
    trackCustomEvent('sendPasswordResetEmail');

    try {
      await UserController.forgotPassword(email);
      notifSuccess();
    } catch (e) {
      setShowError(true);
    } finally {
      setFetching(false);
    }
  };

  const showErrorNotification = () => (
    <Notification handleClose={() => setShowError(false)} />
  );

  const showContentView = () => (
    <div className={styles.containerForm}>
      <div className={classnames(styles.containerTitle)}>
        {backToLogin()}
        <div className={styles.title}>
          <span
            className={classnames(styles.titleText, 'text__heading5__gray800')}
          >
            Reset your password
          </span>
          <div className={styles.containerUserData}>
            <span className="text__body__regular__medium__gray800">
              Enter the email associated with your account, and we will send you
              instructions to reset your password.
            </span>
          </div>
        </div>
      </div>
      <div className={styles.containerInput}>
        <Input
          id="email"
          name="email"
          label="Email"
          placeholder="email@example.com"
          value={email}
          type="email"
          errorMessage={errors.email}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setEmail(e.target.value)
          }
          onBlur={(e) => validateEmail(e.target.value)}
          innerClassName={styles.input}
        />
        <Button
          buttonStyle={ButtonStyle.PrimaryFilled}
          buttonSize={ButtonSize.Medium}
          buttonType={ButtonType.Submit}
          onClick={() => handleSubmit()}
          disabled={!noEmptyFields({ email })}
        >
          {fetching ? (
            <Spinner color="white" className="spinner-inside-button" />
          ) : (
            <> Send instructions </>
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {showError && showErrorNotification()}
      {showContentView()}
    </>
  );
};

export { ResetPasswordStep1 };
